import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash, Check, Calendar, Stack, GraduationCap, BookOpen, Sparkle, PencilSimple } from "@phosphor-icons/react";

const STAGE_LABELS = {
  preschool: "Maternelle",
  primary: "Primaire",
  lower_secondary: "Collège / Basic",
  upper_secondary: "Lycée / Senior",
  tertiary: "Supérieur",
  vocational: "Formation pro.",
};

const PERIOD_TYPE_OPTIONS = [
  { v: "trimester", label: "Trimestres" },
  { v: "semester", label: "Semestres" },
  { v: "term", label: "Terms" },
  { v: "quarter", label: "Quarters" },
  { v: "custom", label: "Personnalisé" },
];

export default function AcademicStructure() {
  const [years, setYears] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [levels, setLevels] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [openYear, setOpenYear] = useState(false);
  const [openPeriod, setOpenPeriod] = useState(false);
  const [openLevel, setOpenLevel] = useState(false);
  const [openCourse, setOpenCourse] = useState(false);
  const [editingYear, setEditingYear] = useState(null);
  const [editingPeriod, setEditingPeriod] = useState(null);
  const [editingLevel, setEditingLevel] = useState(null);
  const [editingCourse, setEditingCourse] = useState(null);
  const [yearForm, setYearForm] = useState({ name: "", start_date: "", end_date: "", is_current: false, status: "active" });
  const [periodForm, setPeriodForm] = useState({ academic_year_id: "", name: "", type: "trimester", sequence: 1, start_date: "", end_date: "", is_current: false });
  const [levelForm, setLevelForm] = useState({ name: "", code: "", stage: "primary", sequence: 1, education_system: "" });
  const [courseForm, setCourseForm] = useState({ academic_year_id: "", subject_id: "", class_group_id: "", teacher_ids: [], coefficient: "", is_optional: false, is_elective: false, group_name: "", weekly_hours: 0, room: "" });

  const resetYearForm = () => setYearForm({ name: "", start_date: "", end_date: "", is_current: false, status: "active" });
  const resetPeriodForm = () => setPeriodForm({ academic_year_id: "", name: "", type: "trimester", sequence: 1, start_date: "", end_date: "", is_current: false });
  const resetLevelForm = () => setLevelForm({ name: "", code: "", stage: "primary", sequence: 1, education_system: "" });
  const resetCourseForm = () => setCourseForm({ academic_year_id: "", subject_id: "", class_group_id: "", teacher_ids: [], coefficient: "", is_optional: false, is_elective: false, group_name: "", weekly_hours: 0, room: "" });

  const openEditYear = (y) => {
    setEditingYear(y);
    setYearForm({ name: y.name || "", start_date: y.start_date || "", end_date: y.end_date || "", is_current: !!y.is_current, status: y.status || "active" });
    setOpenYear(true);
  };
  const openEditPeriod = (p) => {
    setEditingPeriod(p);
    setPeriodForm({ academic_year_id: p.academic_year_id || "", name: p.name || "", type: p.type || "trimester", sequence: p.sequence || 1, start_date: p.start_date || "", end_date: p.end_date || "", is_current: !!p.is_current });
    setOpenPeriod(true);
  };
  const openEditLevel = (l) => {
    setEditingLevel(l);
    setLevelForm({ name: l.name || "", code: l.code || "", stage: l.stage || "primary", sequence: l.sequence || 1, education_system: l.education_system || "" });
    setOpenLevel(true);
  };
  const openEditCourse = (c) => {
    setEditingCourse(c);
    setCourseForm({
      academic_year_id: c.academic_year_id || "",
      subject_id: c.subject_id || "",
      class_group_id: c.class_group_id || "",
      teacher_ids: c.teacher_ids || [],
      coefficient: c.coefficient ?? "",
      is_optional: !!c.is_optional,
      is_elective: !!c.is_elective,
      group_name: c.group_name || "",
      weekly_hours: c.weekly_hours || 0,
      room: c.room || "",
    });
    setOpenCourse(true);
  };

  const currentYear = useMemo(() => years.find(y => y.is_current) || years[0], [years]);

  const loadAll = async () => {
    try {
      const [y, l, s, c, t] = await Promise.all([
        api.get("/academic-years"),
        api.get("/grade-levels"),
        api.get("/subjects"),
        api.get("/classes"),
        // /teachers works for all authed users of the school (director, accountant, admin, super_admin)
        api.get("/teachers"),
      ]);
      setYears(y.data);
      setLevels(l.data);
      setSubjects(s.data);
      setClasses(c.data);
      setTeachers(t.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    if (!currentYear) { setPeriods([]); setCourses([]); return; }
    (async () => {
      try {
        const [p, cr] = await Promise.all([
          api.get(`/academic-periods?academic_year_id=${currentYear.id}`),
          api.get(`/courses?academic_year_id=${currentYear.id}`),
        ]);
        setPeriods(p.data);
        setCourses(cr.data);
      } catch (e) { /* ignore */ }
    })();
  }, [currentYear?.id]);

  // ---------- Years ----------
  const saveYear = async () => {
    try {
      if (editingYear) {
        await api.patch(`/academic-years/${editingYear.id}`, yearForm);
        toast.success("Année modifiée");
      } else {
        await api.post("/academic-years", yearForm);
        toast.success("Année créée");
      }
      setOpenYear(false); setEditingYear(null);
      resetYearForm();
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const setCurrentYear = async (id) => {
    try { await api.post(`/academic-years/${id}/set-current`); toast.success("Année active définie"); loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const delYear = async (id) => {
    if (!window.confirm("Supprimer cette année ?")) return;
    try { await api.delete(`/academic-years/${id}`); loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // ---------- Periods ----------
  const savePeriod = async () => {
    try {
      const payload = { ...periodForm, academic_year_id: periodForm.academic_year_id || currentYear.id };
      if (editingPeriod) {
        await api.patch(`/academic-periods/${editingPeriod.id}`, payload);
        toast.success("Période modifiée");
      } else {
        await api.post("/academic-periods", payload);
        toast.success("Période créée");
      }
      setOpenPeriod(false); setEditingPeriod(null);
      resetPeriodForm();
      const p = await api.get(`/academic-periods?academic_year_id=${currentYear.id}`);
      setPeriods(p.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const delPeriod = async (id) => {
    if (!window.confirm("Supprimer cette période ?")) return;
    try {
      await api.delete(`/academic-periods/${id}`);
      const p = await api.get(`/academic-periods?academic_year_id=${currentYear.id}`);
      setPeriods(p.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ---------- Grade Levels ----------
  const saveLevel = async () => {
    try {
      const payload = { ...levelForm, sequence: Number(levelForm.sequence) };
      if (editingLevel) {
        await api.patch(`/grade-levels/${editingLevel.id}`, payload);
        toast.success("Niveau modifié");
      } else {
        await api.post("/grade-levels", payload);
        toast.success("Niveau créé");
      }
      setOpenLevel(false); setEditingLevel(null);
      resetLevelForm();
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const seedLevels = async () => {
    try {
      const r = await api.post("/grade-levels/seed");
      toast.success(`${r.data.seeded} niveau(x) importé(s) (${r.data.system})`);
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const delLevel = async (id) => {
    if (!window.confirm("Supprimer ce niveau ?")) return;
    try { await api.delete(`/grade-levels/${id}`); loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // ---------- Courses ----------
  const saveCourse = async () => {
    try {
      const payload = {
        ...courseForm,
        academic_year_id: currentYear.id,
        coefficient: courseForm.coefficient === "" ? null : Number(courseForm.coefficient),
        weekly_hours: Number(courseForm.weekly_hours || 0),
      };
      if (editingCourse) {
        await api.patch(`/courses/${editingCourse.id}`, payload);
        toast.success("Cours modifié");
      } else {
        await api.post("/courses", payload);
        toast.success("Cours enregistré");
      }
      setOpenCourse(false); setEditingCourse(null);
      resetCourseForm();
      const cr = await api.get(`/courses?academic_year_id=${currentYear.id}`);
      setCourses(cr.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const delCourse = async (id) => {
    if (!window.confirm("Retirer ce cours ?")) return;
    try {
      await api.delete(`/courses/${id}`);
      const cr = await api.get(`/courses?academic_year_id=${currentYear.id}`);
      setCourses(cr.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleTeacher = (tid) => {
    setCourseForm(f => {
      const list = f.teacher_ids.includes(tid) ? f.teacher_ids.filter(x => x !== tid) : [...f.teacher_ids, tid];
      return { ...f, teacher_ids: list };
    });
  };

  const subjectById = Object.fromEntries(subjects.map(s => [s.id, s]));
  const classById = Object.fromEntries(classes.map(c => [c.id, c]));
  const teacherById = Object.fromEntries(teachers.map(t => [t.id, t]));

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto">
      <header className="mb-6">
        <div className="text-xs uppercase tracking-widest text-secondary font-semibold">Fondation académique</div>
        <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight">Structure académique</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Configurez l'année scolaire active, les périodes d'évaluation, la hiérarchie des niveaux (Maternelle → Terminale ou Grade K → 12) et les cours (matière × classe × enseignant).
        </p>
      </header>

      <Tabs defaultValue="years">
        <TabsList data-testid="tabs-academic">
          <TabsTrigger value="years" data-testid="tab-years"><Calendar size={16} className="mr-1" /> Années ({years.length})</TabsTrigger>
          <TabsTrigger value="periods" data-testid="tab-periods"><Stack size={16} className="mr-1" /> Périodes ({periods.length})</TabsTrigger>
          <TabsTrigger value="levels" data-testid="tab-levels"><GraduationCap size={16} className="mr-1" /> Niveaux ({levels.length})</TabsTrigger>
          <TabsTrigger value="courses" data-testid="tab-courses"><BookOpen size={16} className="mr-1" /> Cours ({courses.length})</TabsTrigger>
        </TabsList>

        {/* --------------- YEARS --------------- */}
        <TabsContent value="years" className="mt-6">
          <div className="flex justify-end mb-4">
            <Dialog open={openYear} onOpenChange={(v) => { setOpenYear(v); if (!v) { setEditingYear(null); resetYearForm(); } }}>
              <DialogTrigger asChild>
                <Button className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-new-year"
                        onClick={() => { setEditingYear(null); resetYearForm(); }}>
                  <Plus size={16} className="mr-2" /> Nouvelle année
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{editingYear ? `Modifier ${editingYear.name}` : "Nouvelle année académique"}</DialogTitle></DialogHeader>
                <div className="grid gap-4">
                  <div><Label>Nom (ex: 2025-2026)</Label><Input value={yearForm.name} onChange={e => setYearForm({ ...yearForm, name: e.target.value })} data-testid="input-year-name" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Début</Label><Input type="date" value={yearForm.start_date} onChange={e => setYearForm({ ...yearForm, start_date: e.target.value })} data-testid="input-year-start" /></div>
                    <div><Label>Fin</Label><Input type="date" value={yearForm.end_date} onChange={e => setYearForm({ ...yearForm, end_date: e.target.value })} data-testid="input-year-end" /></div>
                  </div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={yearForm.is_current} onChange={e => setYearForm({ ...yearForm, is_current: e.target.checked })} data-testid="input-year-current" />
                    Définir comme année active
                  </label>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setOpenYear(false); setEditingYear(null); }}>Annuler</Button>
                  <Button onClick={saveYear} data-testid="btn-save-year">{editingYear ? "Enregistrer" : "Créer"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {years.map(y => (
              <Card key={y.id} className={`p-5 card-hover ${y.is_current ? "border-primary ring-1 ring-primary/40" : ""}`} data-testid={`card-year-${y.id}`}>
                <div className="flex items-start justify-between">
                  <div className="w-10 h-10 rounded-md bg-primary text-primary-foreground flex items-center justify-center"><Calendar size={20} weight="duotone" /></div>
                  <div className="flex gap-1">
                    {!y.is_current && <Button variant="ghost" size="icon" onClick={() => setCurrentYear(y.id)} title="Définir active" data-testid={`btn-year-current-${y.id}`}><Check size={16} className="text-primary" /></Button>}
                    <Button variant="ghost" size="icon" onClick={() => openEditYear(y)} title="Modifier" data-testid={`btn-edit-year-${y.id}`}><PencilSimple size={16} className="text-primary" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => delYear(y.id)}><Trash size={16} className="text-destructive" /></Button>
                  </div>
                </div>
                <div className="mt-3 font-heading text-lg font-semibold flex items-center gap-2">
                  {y.name}
                  {y.is_current && <Badge className="bg-primary text-primary-foreground">Active</Badge>}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {y.start_date || "—"} → {y.end_date || "—"} · <span className="capitalize">{y.status}</span>
                </div>
              </Card>
            ))}
            {!years.length && <div className="col-span-full text-center text-sm text-muted-foreground py-12">Aucune année. Créez la première.</div>}
          </div>
        </TabsContent>

        {/* --------------- PERIODS --------------- */}
        <TabsContent value="periods" className="mt-6">
          {!currentYear ? (
            <div className="text-center text-sm text-muted-foreground py-12">Créez d'abord une année académique.</div>
          ) : (
            <>
              <div className="mb-4 flex items-center justify-between">
                <div className="text-sm text-muted-foreground">Périodes de l'année <b className="text-foreground">{currentYear.name}</b></div>
                <Dialog open={openPeriod} onOpenChange={(v) => { setOpenPeriod(v); if (!v) { setEditingPeriod(null); resetPeriodForm(); } }}>
                  <DialogTrigger asChild>
                    <Button className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-new-period"
                            onClick={() => { setEditingPeriod(null); resetPeriodForm(); }}>
                      <Plus size={16} className="mr-2" /> Nouvelle période
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>{editingPeriod ? `Modifier ${editingPeriod.name}` : "Nouvelle période"}</DialogTitle></DialogHeader>
                    <div className="grid gap-4">
                      <div><Label>Nom (ex: Trimestre 1)</Label><Input value={periodForm.name} onChange={e => setPeriodForm({ ...periodForm, name: e.target.value })} data-testid="input-period-name" /></div>
                      <div>
                        <Label>Type</Label>
                        <Select value={periodForm.type} onValueChange={v => setPeriodForm({ ...periodForm, type: v })}>
                          <SelectTrigger data-testid="select-period-type"><SelectValue /></SelectTrigger>
                          <SelectContent>{PERIOD_TYPE_OPTIONS.map(o => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label>Séquence</Label><Input type="number" min={1} value={periodForm.sequence} onChange={e => setPeriodForm({ ...periodForm, sequence: Number(e.target.value) })} data-testid="input-period-seq" /></div>
                        <label className="flex items-end gap-2 text-sm cursor-pointer pb-2">
                          <input type="checkbox" checked={periodForm.is_current} onChange={e => setPeriodForm({ ...periodForm, is_current: e.target.checked })} data-testid="input-period-current" /> Période active
                        </label>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label>Début</Label><Input type="date" value={periodForm.start_date} onChange={e => setPeriodForm({ ...periodForm, start_date: e.target.value })} /></div>
                        <div><Label>Fin</Label><Input type="date" value={periodForm.end_date} onChange={e => setPeriodForm({ ...periodForm, end_date: e.target.value })} /></div>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => { setOpenPeriod(false); setEditingPeriod(null); }}>Annuler</Button>
                      <Button onClick={savePeriod} data-testid="btn-save-period">{editingPeriod ? "Enregistrer" : "Créer"}</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {periods.map(p => (
                  <Card key={p.id} className={`p-5 card-hover ${p.is_current ? "border-secondary ring-1 ring-secondary/40" : ""}`} data-testid={`card-period-${p.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="w-10 h-10 rounded-md bg-secondary text-secondary-foreground flex items-center justify-center"><Stack size={20} weight="duotone" /></div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEditPeriod(p)} title="Modifier" data-testid={`btn-edit-period-${p.id}`}><PencilSimple size={16} className="text-primary" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => delPeriod(p.id)}><Trash size={16} className="text-destructive" /></Button>
                      </div>
                    </div>
                    <div className="mt-3 font-heading text-lg font-semibold flex items-center gap-2">
                      {p.name}
                      {p.is_current && <Badge variant="outline" className="border-secondary text-secondary">En cours</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Séquence {p.sequence} · <span className="capitalize">{p.type}</span>
                    </div>
                  </Card>
                ))}
                {!periods.length && <div className="col-span-full text-center text-sm text-muted-foreground py-12">Aucune période — la migration en aurait dû créer 3. Rechargez.</div>}
              </div>
            </>
          )}
        </TabsContent>

        {/* --------------- LEVELS --------------- */}
        <TabsContent value="levels" className="mt-6">
          <div className="mb-4 flex items-center justify-between gap-2">
            <div className="text-sm text-muted-foreground">Catalogue des niveaux (Maternelle → Supérieur)</div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={seedLevels} data-testid="btn-seed-levels">
                <Sparkle size={16} className="mr-2" /> Importer selon système
              </Button>
              <Dialog open={openLevel} onOpenChange={(v) => { setOpenLevel(v); if (!v) { setEditingLevel(null); resetLevelForm(); } }}>
                <DialogTrigger asChild>
                  <Button className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-new-level"
                          onClick={() => { setEditingLevel(null); resetLevelForm(); }}>
                    <Plus size={16} className="mr-2" /> Nouveau niveau
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>{editingLevel ? `Modifier ${editingLevel.name}` : "Nouveau niveau"}</DialogTitle></DialogHeader>
                  <div className="grid gap-4">
                    <div><Label>Nom (ex: 6ème, Grade 6, Form 2)</Label><Input value={levelForm.name} onChange={e => setLevelForm({ ...levelForm, name: e.target.value })} data-testid="input-level-name" /></div>
                    <div><Label>Code</Label><Input value={levelForm.code} onChange={e => setLevelForm({ ...levelForm, code: e.target.value })} data-testid="input-level-code" /></div>
                    <div>
                      <Label>Étape</Label>
                      <Select value={levelForm.stage} onValueChange={v => setLevelForm({ ...levelForm, stage: v })}>
                        <SelectTrigger data-testid="select-level-stage"><SelectValue /></SelectTrigger>
                        <SelectContent>{Object.entries(STAGE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Séquence (ordre)</Label><Input type="number" value={levelForm.sequence} onChange={e => setLevelForm({ ...levelForm, sequence: e.target.value })} data-testid="input-level-seq" /></div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => { setOpenLevel(false); setEditingLevel(null); }}>Annuler</Button>
                    <Button onClick={saveLevel} data-testid="btn-save-level">{editingLevel ? "Enregistrer" : "Créer"}</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">Ordre</th><th className="px-4 py-3">Niveau</th><th className="px-4 py-3">Code</th><th className="px-4 py-3">Étape</th><th className="px-4 py-3 text-right"></th>
              </tr></thead>
              <tbody>
                {levels.map((g, i) => (
                  <tr key={g.id} className={`border-b border-border last:border-0 ${i % 2 ? "bg-muted/30" : ""}`} data-testid={`row-level-${g.id}`}>
                    <td className="px-4 py-3 tabular-nums">{g.sequence}</td>
                    <td className="px-4 py-3 font-medium">{g.name}</td>
                    <td className="px-4 py-3 font-mono text-xs">{g.code || "—"}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{STAGE_LABELS[g.stage] || g.stage}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEditLevel(g)} title="Modifier" data-testid={`btn-edit-level-${g.id}`}><PencilSimple size={16} className="text-primary" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => delLevel(g.id)}><Trash size={16} className="text-destructive" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!levels.length && <tr><td colSpan={5} className="px-4 py-12 text-center text-sm text-muted-foreground">Aucun niveau. Cliquez "Importer selon système" pour utiliser la structure de votre pays.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* --------------- COURSES --------------- */}
        <TabsContent value="courses" className="mt-6">
          {!currentYear ? (
            <div className="text-center text-sm text-muted-foreground py-12">Créez d'abord une année académique.</div>
          ) : (
            <>
              {/* KPIs de l'année en cours */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                <Card className="p-4" data-testid="kpi-courses">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Cours actifs</div>
                  <div className="font-heading text-2xl font-bold mt-1 tabular-nums">{courses.length}</div>
                </Card>
                <Card className="p-4" data-testid="kpi-teachers">
                  <div className="text-[10px] uppercase tracking-wider text-secondary font-semibold">Enseignants de l'école</div>
                  <div className="font-heading text-2xl font-bold mt-1 tabular-nums flex items-baseline gap-2">
                    {teachers.length}
                    <span className="text-xs text-muted-foreground font-normal">{teachers.length > 1 ? "profs" : "prof"}</span>
                  </div>
                </Card>
                <Card className="p-4" data-testid="kpi-classes">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Classes</div>
                  <div className="font-heading text-2xl font-bold mt-1 tabular-nums">{classes.length}</div>
                </Card>
                <Card className="p-4" data-testid="kpi-subjects">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Matières</div>
                  <div className="font-heading text-2xl font-bold mt-1 tabular-nums">{subjects.length}</div>
                </Card>
              </div>

              <div className="mb-4 flex items-center justify-between">
                <div className="text-sm text-muted-foreground">Cours de l'année <b className="text-foreground">{currentYear.name}</b> — matière × classe × enseignant(s)</div>
                <Dialog open={openCourse} onOpenChange={(v) => { setOpenCourse(v); if (!v) { setEditingCourse(null); resetCourseForm(); } }}>
                  <DialogTrigger asChild>
                    <Button className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-new-course"
                            onClick={() => { setEditingCourse(null); resetCourseForm(); }}>
                      <Plus size={16} className="mr-2" /> Nouveau cours
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>{editingCourse ? "Modifier le cours" : "Nouveau cours"}</DialogTitle></DialogHeader>
                    <div className="grid gap-4">
                      <div>
                        <Label>Matière</Label>
                        <Select value={courseForm.subject_id} onValueChange={v => setCourseForm({ ...courseForm, subject_id: v })}>
                          <SelectTrigger data-testid="select-course-subject"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                          <SelectContent>{subjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name} ({s.code})</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Classe</Label>
                        <Select value={courseForm.class_group_id} onValueChange={v => setCourseForm({ ...courseForm, class_group_id: v })}>
                          <SelectTrigger data-testid="select-course-class"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                          <SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Enseignants (un ou plusieurs)</Label>
                        <div className="border border-border rounded-md p-2 max-h-40 overflow-y-auto space-y-1">
                          {teachers.length === 0 && <div className="text-xs text-muted-foreground p-2">Aucun enseignant enregistré.</div>}
                          {teachers.map(t => (
                            <label key={t.id} className="flex items-center gap-2 text-sm p-1 hover:bg-muted rounded cursor-pointer">
                              <input type="checkbox" checked={courseForm.teacher_ids.includes(t.id)} onChange={() => toggleTeacher(t.id)} data-testid={`chk-teacher-${t.id}`} />
                              {t.name} <span className="text-xs text-muted-foreground">{t.email}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label>Coef. (optionnel)</Label><Input type="number" step="0.5" value={courseForm.coefficient} onChange={e => setCourseForm({ ...courseForm, coefficient: e.target.value })} data-testid="input-course-coef" /></div>
                        <div><Label>Heures/sem.</Label><Input type="number" step="0.5" value={courseForm.weekly_hours} onChange={e => setCourseForm({ ...courseForm, weekly_hours: e.target.value })} /></div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label>Groupe (ex: A, B)</Label><Input value={courseForm.group_name} onChange={e => setCourseForm({ ...courseForm, group_name: e.target.value })} /></div>
                        <div><Label>Salle</Label><Input value={courseForm.room} onChange={e => setCourseForm({ ...courseForm, room: e.target.value })} /></div>
                      </div>
                      <div className="flex gap-4 text-sm">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={courseForm.is_optional} onChange={e => setCourseForm({ ...courseForm, is_optional: e.target.checked })} data-testid="chk-course-optional" /> Optionnel
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={courseForm.is_elective} onChange={e => setCourseForm({ ...courseForm, is_elective: e.target.checked })} data-testid="chk-course-elective" /> Électif
                        </label>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => { setOpenCourse(false); setEditingCourse(null); }}>Annuler</Button>
                      <Button onClick={saveCourse} data-testid="btn-save-course">{editingCourse ? "Enregistrer" : "Enregistrer"}</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="bg-card border border-border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-3">Matière</th><th className="px-4 py-3">Classe</th><th className="px-4 py-3">Enseignant(s)</th><th className="px-4 py-3">Coef.</th><th className="px-4 py-3">Options</th><th className="px-4 py-3 text-right"></th>
                  </tr></thead>
                  <tbody>
                    {courses.map((cr, i) => (
                      <tr key={cr.id} className={`border-b border-border last:border-0 ${i % 2 ? "bg-muted/30" : ""}`} data-testid={`row-course-${cr.id}`}>
                        <td className="px-4 py-3 font-medium">{subjectById[cr.subject_id]?.name || <em className="text-muted-foreground">—</em>}</td>
                        <td className="px-4 py-3">{classById[cr.class_group_id]?.name || <em className="text-muted-foreground">—</em>}{cr.group_name ? ` · ${cr.group_name}` : ""}</td>
                        <td className="px-4 py-3 text-xs">{(cr.teacher_ids || []).map(tid => teacherById[tid]?.name || "?").join(", ") || <em className="text-muted-foreground">non affecté</em>}</td>
                        <td className="px-4 py-3 tabular-nums">{cr.coefficient ?? subjectById[cr.subject_id]?.coefficient ?? "—"}</td>
                        <td className="px-4 py-3 text-xs">
                          {cr.is_optional && <Badge variant="outline" className="mr-1">Optionnel</Badge>}
                          {cr.is_elective && <Badge variant="outline">Électif</Badge>}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openEditCourse(cr)} title="Modifier" data-testid={`btn-edit-course-${cr.id}`}><PencilSimple size={16} className="text-primary" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => delCourse(cr.id)}><Trash size={16} className="text-destructive" /></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!courses.length && <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-muted-foreground">Aucun cours affecté à cette année.</td></tr>}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
